import { AvailabilityCalendar } from "@/components/admin/AvailabilityCalendar";
import { GuestEditDialog } from "@/components/admin/GuestEditDialog";
import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { ReservationFormDialog } from "@/components/admin/ReservationFormDialog";
import { BrandLoader } from "@/components/brand/BrandLoader";
import { EmptyState } from "@/components/brand/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { guestService, reservationService, roomService, roomTypeService } from "@/lib/api";
import { formatDate, formatKrw, formatTime } from "@/lib/format";
import { useSelectedProperty } from "@/stores/property";
import type { GuestUpdate, Reservation, ReservationCreate, Room } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ClipboardList, Copy, LinkIcon, MessageSquare, Pencil, Plus, Send } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  booked: "예약",
  checked_in: "체크인",
  checked_out: "체크아웃",
  cancelled: "취소",
};

const STATUS_CLASS: Record<string, string> = {
  booked: "bg-blue-100 text-blue-700",
  checked_in: "bg-green-100 text-green-700",
  checked_out: "bg-muted text-muted-foreground",
  cancelled: "bg-red-100 text-red-600",
};

// 환영 알림(안내 링크 메일) 발송 상태. 미발송(null)은 "-" 로 둔다.
const WELCOME_LABEL: Record<string, string> = {
  queued: "발송 중",
  sent: "발송함",
  failed: "발송 실패",
};

const WELCOME_CLASS: Record<string, string> = {
  queued: "bg-amber-100 text-amber-700",
  sent: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-600",
};

function formatDateRange(checkIn: string, checkOut: string) {
  return `${checkIn} ~ ${checkOut}`;
}

function channelLabel(channel: string) {
  if (channel === "direct") return "직접";
  if (channel.startsWith("channex:")) return channel.replace("channex:", "OTA:");
  return channel;
}

// ─── Check-in assign-room dialog ─────────────────────────────────────────────

interface CheckInDialogProps {
  reservation: Reservation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eligibleRooms: Room[];
  isPending: boolean;
  onConfirm: (room_id: string) => void;
}

function CheckInDialog({
  reservation,
  open,
  onOpenChange,
  eligibleRooms,
  isPending,
  onConfirm,
}: CheckInDialogProps) {
  const [selectedRoom, setSelectedRoom] = useState<string>("");

  // Reset when opened for a new reservation
  const handleOpenChange = (next: boolean) => {
    if (next) setSelectedRoom("");
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>체크인 — 호실 배정</DialogTitle>
          <DialogDescription>
            {reservation ? `${reservation.guest_name} 게스트에게 호실을 배정합니다.` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label htmlFor="ci-room">호실 선택 *</Label>
          <Select value={selectedRoom} onValueChange={setSelectedRoom}>
            <SelectTrigger id="ci-room">
              <SelectValue placeholder="호실 선택…" />
            </SelectTrigger>
            <SelectContent>
              {eligibleRooms.length > 0 ? (
                eligibleRooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="__none__" disabled>
                  배정 가능한 호실 없음
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            disabled={!selectedRoom || isPending}
            onClick={() => onConfirm(selectedRoom)}
          >
            체크인 확인
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ReservationsPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [checkInTarget, setCheckInTarget] = useState<Reservation | null>(null);
  const [checkInOpen, setCheckInOpen] = useState(false);

  const propertyId = useSelectedProperty();

  const { data: reservations, isLoading: loadingReservations } = useQuery({
    queryKey: ["reservations", propertyId],
    queryFn: () => reservationService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });

  const { data: roomTypes = [] } = useQuery({
    queryKey: ["room-types", propertyId],
    queryFn: () => roomTypeService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });

  const { data: rooms = [] } = useQuery({
    queryKey: ["rooms", propertyId],
    queryFn: () => roomService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["reservations"] });
    queryClient.invalidateQueries({ queryKey: ["room-types"] });
    queryClient.invalidateQueries({ queryKey: ["rooms"] });
    queryClient.invalidateQueries({ queryKey: ["room-type-availability"] });
  };

  // ── Create ──────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (body: ReservationCreate) => reservationService.create(body),
    onSuccess: () => {
      invalidateAll();
      setCreateOpen(false);
      toast.success("예약이 등록되었습니다.");
    },
    onError: (error) => {
      const detail = isAxiosError(error) ? error.response?.data?.detail : null;
      if (isAxiosError(error) && error.response?.status === 409) {
        toast.error(typeof detail === "string" ? detail : "가용 객실이 없습니다");
      } else {
        toast.error("등록에 실패했습니다.");
      }
    },
  });

  const handleCreate = (values: Omit<ReservationCreate, "property_id">) => {
    if (!propertyId) return;
    createMutation.mutate({ ...values, property_id: propertyId });
  };

  // ── Check-in ─────────────────────────────────────────────────────────────
  const checkInMutation = useMutation({
    mutationFn: ({ id, room_id }: { id: string; room_id: string }) =>
      reservationService.checkIn(id, room_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      queryClient.invalidateQueries({ queryKey: ["room-type-availability"] });
      setCheckInOpen(false);
      setCheckInTarget(null);
      toast.success("체크인 처리되었습니다.");
    },
    onError: (error) => {
      const detail = isAxiosError(error) ? error.response?.data?.detail : null;
      if (isAxiosError(error) && error.response?.status === 409) {
        toast.error(typeof detail === "string" ? detail : "이미 배정된 호실입니다.");
      } else {
        toast.error("체크인에 실패했습니다.");
      }
    },
  });

  const openCheckIn = (reservation: Reservation) => {
    setCheckInTarget(reservation);
    setCheckInOpen(true);
  };

  const handleCheckIn = (room_id: string) => {
    if (!checkInTarget) return;
    checkInMutation.mutate({ id: checkInTarget.id, room_id });
  };

  // ── Check-out ─────────────────────────────────────────────────────────────
  const checkOutMutation = useMutation({
    mutationFn: (id: string) => reservationService.checkOut(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      queryClient.invalidateQueries({ queryKey: ["room-type-availability"] });
      toast.success("체크아웃 처리되었습니다.");
    },
    onError: () => toast.error("체크아웃에 실패했습니다."),
  });

  const handleCheckOut = (reservation: Reservation) => {
    if (!confirm(`${reservation.guest_name} 게스트를 체크아웃 처리할까요?`)) return;
    checkOutMutation.mutate(reservation.id);
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const cancelMutation = useMutation({
    mutationFn: (id: string) => reservationService.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      queryClient.invalidateQueries({ queryKey: ["room-type-availability"] });
      toast.success("예약이 취소되었습니다.");
    },
    onError: () => toast.error("취소에 실패했습니다."),
  });

  const handleCancel = (reservation: Reservation) => {
    if (!confirm(`${reservation.guest_name} 게스트의 예약을 취소할까요?`)) return;
    cancelMutation.mutate(reservation.id);
  };

  // ── Guest link / edit ─────────────────────────────────────────────────────
  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("게스트 챗봇 링크가 복사되었습니다.");
    } catch {
      toast.error("복사에 실패했습니다.");
    }
  };

  const issueGuestMutation = useMutation({
    mutationFn: (id: string) => reservationService.issueGuest(id),
    onSuccess: (reservation) => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      const url = reservation.guest?.link_url;
      toast.success("게스트 챗봇 링크가 발급되었습니다.", {
        description: url,
        action: url ? { label: "챗봇 링크 복사", onClick: () => copyLink(url) } : undefined,
      });
    },
    onError: () => toast.error("링크 발급에 실패했습니다."),
  });

  // 손으로 링크를 복사하지 않도록, 안내 메일을 여기서 (재)발송한다.
  const sendWelcomeMutation = useMutation({
    mutationFn: (id: string) => reservationService.sendWelcome(id),
    onSuccess: (reservation) => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      const guest = reservation.guest;
      if (guest?.welcome_status === "sent") {
        toast.success("안내 메일을 보냈어요.");
      } else {
        // 실패도 결과다 — 사유를 그대로 보여주고 행에도 남긴다.
        toast.error(guest?.welcome_error ?? "안내 메일을 보내지 못했어요.");
      }
    },
    onError: () => toast.error("안내 메일을 보내지 못했어요."),
  });

  const [editGuestId, setEditGuestId] = useState<string | null>(null);

  const guestUpdateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: GuestUpdate }) => guestService.update(id, body),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      queryClient.invalidateQueries({ queryKey: ["guest", id] });
      setEditGuestId(null);
      toast.success("저장되었습니다.");
    },
    onError: () => toast.error("저장에 실패했습니다."),
  });

  const handleGuestUpdate = (body: GuestUpdate) => {
    if (!editGuestId) return;
    guestUpdateMutation.mutate({ id: editGuestId, body });
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const roomTypeMap = new Map(roomTypes.map((rt) => [rt.id, rt.name]));

  const occupiedRoomIds = new Set(
    (reservations ?? [])
      .filter((r) => r.status === "checked_in" && r.room_id)
      .map((r) => r.room_id as string)
  );

  const eligibleRooms = rooms.filter(
    (r) =>
      r.is_active &&
      checkInTarget != null &&
      r.room_type_id === checkInTarget.room_type_id &&
      !occupiedRoomIds.has(r.id)
  );

  const isLoading = loadingReservations;

  if (!propertyId) {
    return <NoPropertySelected />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">예약 · 프런트</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            예약 관리, 체크인·아웃을 한눈에 처리합니다.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> 새 예약
        </Button>
      </div>

      {/* Reservation list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4" />
            예약 목록
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <BrandLoader className="py-12" size={44} label="불러오는 중…" />
          ) : reservations && reservations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>게스트명</TableHead>
                  <TableHead>연락처</TableHead>
                  <TableHead>환영 알림</TableHead>
                  <TableHead>객실타입</TableHead>
                  <TableHead>체크인~체크아웃</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>채널</TableHead>
                  <TableHead className="text-right">금액</TableHead>
                  <TableHead className="text-right">액션</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reservations.map((rsv) => {
                  const isMuted = rsv.status === "checked_out" || rsv.status === "cancelled";
                  return (
                    <TableRow key={rsv.id} className={isMuted ? "opacity-60" : ""}>
                      <TableCell className="font-medium">{rsv.guest_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {rsv.guest?.email ?? rsv.guest?.phone ?? "-"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {rsv.guest ? (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5">
                              {rsv.guest.welcome_status ? (
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${
                                    WELCOME_CLASS[rsv.guest.welcome_status] ??
                                    "bg-muted text-muted-foreground"
                                  }`}
                                >
                                  {WELCOME_LABEL[rsv.guest.welcome_status] ??
                                    rsv.guest.welcome_status}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">미발송</span>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={sendWelcomeMutation.isPending}
                                onClick={() => sendWelcomeMutation.mutate(rsv.id)}
                              >
                                <Send className="h-4 w-4" />
                                {rsv.guest.welcome_status === "sent" ? "재발송" : "발송"}
                              </Button>
                            </div>
                            {rsv.guest.welcome_at && (
                              <p className="tabular-nums text-muted-foreground">
                                {formatDate(rsv.guest.welcome_at)} {formatTime(rsv.guest.welcome_at)}
                              </p>
                            )}
                            {rsv.guest.welcome_error && (
                              <p className="text-destructive">{rsv.guest.welcome_error}</p>
                            )}
                            {/* 자동 재발송하지 않는다 — 기간을 손볼 때마다 게스트 편지함이
                                울린다. 어긋남만 알리고 [재발송] 은 운영자가 고른다. */}
                            {rsv.guest.welcome_expiry_stale && (
                              <p className="text-amber-600">
                                기간이 바뀌어 안내한 만료일과 달라요
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{roomTypeMap.get(rsv.room_type_id) ?? rsv.room_type_id}</TableCell>
                      <TableCell className="text-sm tabular-nums">
                        {formatDateRange(rsv.check_in, rsv.check_out)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            STATUS_CLASS[rsv.status] ?? "bg-muted text-muted-foreground"
                          }`}
                        >
                          {STATUS_LABEL[rsv.status] ?? rsv.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {channelLabel(rsv.channel)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {formatKrw(rsv.total_amount)}
                      </TableCell>
                      <TableCell>
                        {/* Variable-width status actions first, fixed trio last, so every
                            row's trailing buttons line up down the column. */}
                        <div className="flex items-center justify-end gap-1">
                          {rsv.status === "booked" && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openCheckIn(rsv)}>
                                체크인
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleCancel(rsv)}
                              >
                                취소
                              </Button>
                            </>
                          )}
                          {rsv.status === "checked_in" && (
                            <Button size="sm" variant="outline" onClick={() => handleCheckOut(rsv)}>
                              체크아웃
                            </Button>
                          )}
                          {rsv.guest ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyLink(rsv.guest?.link_url ?? "")}
                              >
                                <Copy className="h-4 w-4" /> 챗봇 링크 복사
                              </Button>
                              <Button variant="ghost" size="icon" aria-label="대화 보기" asChild>
                                <Link to={`/admin/conversations?guest_id=${rsv.guest.id}`}>
                                  <MessageSquare className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="게스트 편집"
                                onClick={() => setEditGuestId(rsv.guest?.id ?? null)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={issueGuestMutation.isPending}
                              onClick={() => issueGuestMutation.mutate(rsv.id)}
                            >
                              <LinkIcon className="h-4 w-4" /> 챗봇 링크 발급
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="px-6 pb-6">
              <EmptyState
                title="아직 등록된 예약이 없어요"
                description="새 예약 버튼으로 직접 예약을 등록하거나 OTA 채널을 연동하면 예약이 표시됩니다."
                action={
                  <Button onClick={() => setCreateOpen(true)}>
                    <Plus className="h-4 w-4" /> 새 예약
                  </Button>
                }
              />
            </div>
          )}
        </CardContent>
      </Card>

      <AvailabilityCalendar roomTypes={roomTypes} />

      {/* Dialogs */}
      <ReservationFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        roomTypes={roomTypes}
        isPending={createMutation.isPending}
        onSubmit={handleCreate}
      />

      <GuestEditDialog
        open={editGuestId != null}
        onOpenChange={(open) => {
          if (!open) setEditGuestId(null);
        }}
        guestId={editGuestId}
        isPending={guestUpdateMutation.isPending}
        onSubmit={handleGuestUpdate}
      />

      <CheckInDialog
        open={checkInOpen}
        onOpenChange={(open) => {
          setCheckInOpen(open);
          if (!open) setCheckInTarget(null);
        }}
        reservation={checkInTarget}
        eligibleRooms={eligibleRooms}
        isPending={checkInMutation.isPending}
        onConfirm={handleCheckIn}
      />
    </div>
  );
}
